import {
  collection, doc, addDoc, updateDoc, deleteDoc, getDoc, getDocs, setDoc, query, where, orderBy,
  onSnapshot, serverTimestamp, runTransaction, limit
} from 'firebase/firestore';
import { db } from '../firebase';

const cleanNumber = (v) => parseFloat(v) || 0;

// ---------- Caisse ----------
export class CaisseService {
  static async getConfig(cabinetId) {
    try {
      const snap = await getDoc(doc(db, 'caisse_config', cabinetId));
      return { success: true, config: snap.exists() ? snap.data() : { initialAmount: 0, resetAt: null } };
    } catch (error) {
      console.error('Erreur caisse config:', error);
      return { success: false, error: error.message, config: { initialAmount: 0, resetAt: null } };
    }
  }

  // Définir le montant initial = nouveau point de départ (resetAt)
  static async setInitialAmount(cabinetId, amount, userUid) {
    try {
      await setDoc(doc(db, 'caisse_config', cabinetId), {
        initialAmount: cleanNumber(amount),
        resetAt: new Date().toISOString(),
        updatedAt: serverTimestamp(),
        updatedBy: userUid || null
      }, { merge: true });
      return { success: true };
    } catch (error) {
      console.error('Erreur caisse:', error);
      return { success: false, error: error.message };
    }
  }
}

// ---------- Dépenses ----------
export class DepenseService {
  static subscribe(cabinetId, callback) {
    try {
      const q = query(collection(db, 'depenses'), where('cabinetId', '==', cabinetId), orderBy('date', 'desc'), limit(500));
      return onSnapshot(q, (snap) => {
        const items = [];
        snap.forEach((d) => items.push({ id: d.id, ...d.data() }));
        callback(items);
      }, (error) => {
        console.error('Erreur dépenses:', error);
        callback([]);
      });
    } catch (error) {
      console.error('Erreur config dépenses:', error);
      return () => {};
    }
  }

  static async add(cabinetId, data, user) {
    try {
      const ref = await addDoc(collection(db, 'depenses'), {
        cabinetId,
        montant: cleanNumber(data.montant),
        date: data.date || new Date().toISOString().slice(0, 10),
        categorie: data.categorie || 'Autre',
        description: data.description || '',
        depuisCaisse: data.depuisCaisse === true,
        modePaiement: data.modePaiement || 'espece',
        projectId: data.projectId || null,
        createdBy: user?.uid || null,
        createdByName: user?.name || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { success: true, id: ref.id };
    } catch (error) {
      console.error('Erreur ajout dépense:', error);
      return { success: false, error: error.message };
    }
  }

  static async update(id, data) {
    try {
      const payload = { ...data, updatedAt: serverTimestamp() };
      if ('montant' in payload) payload.montant = cleanNumber(payload.montant);
      delete payload.id;
      delete payload.cabinetId;
      await updateDoc(doc(db, 'depenses', id), payload);
      return { success: true };
    } catch (error) {
      console.error('Erreur MAJ dépense:', error);
      return { success: false, error: error.message };
    }
  }

  static async remove(id) {
    try {
      await deleteDoc(doc(db, 'depenses', id));
      return { success: true };
    } catch (error) {
      console.error('Erreur suppr dépense:', error);
      return { success: false, error: error.message };
    }
  }
}

// ---------- Devis / Factures ----------
export class FactureService {
  static subscribe(cabinetId, callback) {
    try {
      const q = query(collection(db, 'factures'), where('cabinetId', '==', cabinetId), orderBy('createdAt', 'desc'), limit(500));
      return onSnapshot(q, (snap) => {
        const items = [];
        snap.forEach((d) => items.push({ id: d.id, ...d.data() }));
        callback(items);
      }, (error) => {
        console.error('Erreur factures:', error);
        callback([]);
      });
    } catch (error) {
      console.error('Erreur config factures:', error);
      return () => {};
    }
  }

  // Numéro NN/AAAA par cabinet, par type et par année (transaction sur counters/{cabinetId})
  static async nextNumber(cabinetId, type, year) {
    const key = `${type}_${year}`;
    const ref = doc(db, 'counters', cabinetId);
    const n = await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      const current = snap.exists() ? (snap.data()[key] || 0) : 0;
      const next = current + 1;
      tx.set(ref, { [key]: next, updatedAt: serverTimestamp() }, { merge: true });
      return next;
    });
    return `${String(n).padStart(2, '0')}/${year}`;
  }

  static async create(cabinetId, data, user) {
    try {
      const date = data.date || new Date().toISOString().slice(0, 10);
      const year = date.slice(0, 4);
      const type = data.type === 'facture' ? 'facture' : 'devis';
      const numero = await FactureService.nextNumber(cabinetId, type, year);
      const ref = await addDoc(collection(db, 'factures'), {
        cabinetId,
        type,
        numero,
        date,
        projectId: data.projectId || null,
        refGlobal: data.refGlobal || '',
        clientNom: data.clientNom || '',
        clientAdresse: data.clientAdresse || '',
        clientIdentifiant: data.clientIdentifiant || '',
        objet: data.objet || '',
        lignes: (data.lignes || []).map((l) => ({
          designation: l.designation || '',
          unite: l.unite || 'F',
          quantite: cleanNumber(l.quantite) || 1,
          prixUnitaire: cleanNumber(l.prixUnitaire)
        })),
        espece: data.espece === true,
        fraisTimbre: cleanNumber(data.fraisTimbre),
        totalFinal: cleanNumber(data.totalFinal) || cleanNumber(data.montantTTC),
        tauxTVA: cleanNumber(data.tauxTVA),
        montantHT: cleanNumber(data.montantHT),
        montantTVA: cleanNumber(data.montantTVA),
        montantTTC: cleanNumber(data.montantTTC),
        statut: data.statut || 'brouillon',
        devisId: data.devisId || null,
        notes: data.notes || '',
        createdBy: user?.uid || null,
        createdByName: user?.name || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { success: true, id: ref.id, numero };
    } catch (error) {
      console.error('Erreur création facture:', error);
      return { success: false, error: error.message };
    }
  }

  static async update(id, data) {
    try {
      const payload = { ...data, updatedAt: serverTimestamp() };
      delete payload.id;
      delete payload.cabinetId;
      delete payload.numero;
      delete payload.type;
      await updateDoc(doc(db, 'factures', id), payload);
      return { success: true };
    } catch (error) {
      console.error('Erreur MAJ facture:', error);
      return { success: false, error: error.message };
    }
  }

  static async remove(id) {
    try {
      await deleteDoc(doc(db, 'factures', id));
      return { success: true };
    } catch (error) {
      console.error('Erreur suppr facture:', error);
      return { success: false, error: error.message };
    }
  }

  // Devis → facture (nouveau numéro de facture, le devis passe en « accepté »)
  static async convertToFacture(cabinetId, devis, user) {
    const result = await FactureService.create(cabinetId, {
      ...devis, type: 'facture', statut: 'envoyee', devisId: devis.id, date: new Date().toISOString().slice(0, 10)
    }, user);
    if (result.success) {
      await FactureService.update(devis.id, { statut: 'accepte', factureId: result.id });
    }
    return result;
  }

  static async getAll(cabinetId) {
    const snap = await getDocs(query(collection(db, 'factures'), where('cabinetId', '==', cabinetId), orderBy('createdAt', 'desc')));
    const items = [];
    snap.forEach((d) => items.push({ id: d.id, ...d.data() }));
    return items;
  }
}
